import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminAPI } from "@/features/admin/api/admin";
import { downloadBlob } from "@/features/utils/blob";
import { useState } from "react";
import { toast } from "sonner";

type Format = "sessions" | "messages";

/** `YYYY-MM-DD` of a local date, the value an `<input type="date">` holds. */
const toInputDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Local midnight of an input date, `days` later — `setDate` so a DST switch is a day. */
const midnight = (value: string, days = 0): number => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.getTime();
};

/**
 * Every conversation of this client over a window, downloaded as a CSV to be read
 * afterwards — in a spreadsheet, a notebook, a conversation with a model.
 *
 * Two files, because they answer two questions: one row per conversation (what to filter
 * and count on), or every message of each (what to read once the first said which
 * conversations matter). Both bounds are whole days in the browser's timezone, the end day
 * included.
 */
export function SessionsExportCard({ clientId }: { clientId: string }) {
  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setDate(today.getDate() - 30);

  const [from, setFrom] = useState(toInputDate(monthAgo));
  const [to, setTo] = useState(toInputDate(today));
  const [pending, setPending] = useState<Format | null>(null);

  const invalid = !from || !to || from > to;

  const download = async (format: Format) => {
    setPending(format);
    try {
      const blob = await AdminAPI.getTenantSessionsCsv(clientId, {
        from: midnight(from),
        to: midnight(to, 1),
        format,
      });
      downloadBlob(
        blob,
        `${format === "messages" ? "messages" : "conversations"}_${clientId}_${from}_${to}.csv`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Export impossible");
    } finally {
      setPending(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Export des conversations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="sessions-export-from">Du</Label>
            <Input
              id="sessions-export-from"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sessions-export-to">Au (inclus)</Label>
            <Input
              id="sessions-export-to"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            disabled={invalid || pending !== null}
            onClick={() => download("sessions")}
            title="Date, canal, agent, client, résultat, résumé, durées, erreurs d'outils…"
          >
            {pending === "sessions"
              ? "Export en cours…"
              : "Une ligne par conversation"}
          </Button>
          <Button
            variant="outline"
            disabled={invalid || pending !== null}
            onClick={() => download("messages")}
            title="Les mêmes colonnes que l'export d'une conversation, précédées de la conversation"
          >
            {pending === "messages"
              ? "Export en cours…"
              : "Une ligne par message"}
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">
          Conversations commencées sur la période (jours entiers, fuseau de ce
          navigateur). Un an et 20 000 conversations au plus par export.
        </p>
      </CardContent>
    </Card>
  );
}
